import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
// eslint-disable-next-line no-unused-vars
import Chart from 'chart.js/auto';
import Loader from '../../../Components/utils/Loader';
import { FiUsers, FiShoppingCart, FiPackage, FiAlertTriangle, FiTrendingUp, FiTrendingDown, FiDownload } from 'react-icons/fi';
import { CSVLink } from 'react-csv';
import { exportToPDF } from '../../../utils/exportUtils';

const API = process.env.REACT_APP_Backend_Host_Name;

function AnalyticsOverview({ query }) {
    const [data, setData] = useState(null);
    const [activityData, setActivityData] = useState(null);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('adminToken');

    useEffect(() => {
        setLoading(true);
        Promise.all([
            axios.get(`${API}/api/v1/settings/admin/analytics/overview?${query}`, { headers: { Authorization: `Admin ${token}` } }),
            axios.get(`${API}/api/v1/settings/admin/analytics/activity?${query}`, { headers: { Authorization: `Admin ${token}` } })
        ]).then(([ovRes, actRes]) => {
            setData(ovRes.data.overview);
            setActivityData(actRes.data);
        }).finally(() => setLoading(false));
    }, [query]);

    if (loading) return <Loader type="simpleMax" />;
    if (!data) return null;

    const o = data;

    const cards = [
        { label: 'Total Users', value: o.totalUsers, sub: `+${o.newUsers} new`, icon: <FiUsers />, color: '#6366f1', growth: o.userGrowth },
        { label: 'Total Sellers', value: o.totalSellers, sub: `${o.paidSellers} paid`, icon: <FiUsers />, color: '#8b5cf6' },
        { label: 'Total Orders', value: o.totalOrders, sub: `+${o.periodOrders} this period`, icon: <FiShoppingCart />, color: '#0ea5e9', growth: o.orderGrowth },
        { label: 'Active Orders', value: o.activeOrders, sub: `${o.completedOrders} completed`, icon: <FiShoppingCart />, color: '#10b981' },
        { label: 'Total Listings', value: o.totalListings, sub: `+${o.newListings} new`, icon: <FiPackage />, color: '#f59e0b' },
        { label: 'Avg Order Value', value: `$${o.avgOrderValue}`, sub: `${o.periodOrders} orders`, icon: <FiShoppingCart />, color: '#ec4899' },
        { label: 'Cancelled Orders', value: o.cancelledOrders, icon: <FiAlertTriangle />, color: '#ef4444' },
        { label: 'Open Disputes', value: o.openDisputes, sub: `${o.resolvedDisputes} resolved`, icon: <FiAlertTriangle />, color: '#f97316' },
    ];

    const activityIcons = { signup: '👤', order: '🛒', listing: '📦', dispute: '⚠️' };
    const activityColors = { signup: '#6366f1', order: '#0ea5e9', listing: '#10b981', dispute: '#f97316' };

    // Mini chart for hourly activity
    const hourlyChart = activityData ? {
        labels: activityData.hourlyActivity.map(h => `${h.hour}:00`),
        datasets: [{
            label: 'Orders by Hour',
            data: activityData.hourlyActivity.map(h => h.count),
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99,102,241,0.1)',
            fill: true, tension: 0.4, pointRadius: 2,
        }]
    } : null;

    const miniChartOpts = {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { display: false } },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 8 } },
            y: { grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 } }, beginAtZero: true }
        }
    };

    const csvData = data ? [
        { Metric: 'Total Users', Value: o.totalUsers, Details: `+${o.newUsers} new` },
        { Metric: 'Total Sellers', Value: o.totalSellers, Details: `${o.paidSellers} paid` },
        { Metric: 'Total Orders', Value: o.totalOrders, Details: `+${o.periodOrders} this period` },
        { Metric: 'Active Orders', Value: o.activeOrders, Details: `${o.completedOrders} completed` },
        { Metric: 'Total Listings', Value: o.totalListings, Details: `+${o.newListings} new` },
        { Metric: 'Avg Order Value', Value: `$${o.avgOrderValue}`, Details: `${o.periodOrders} orders` },
        { Metric: 'Cancelled Orders', Value: o.cancelledOrders, Details: '' },
        { Metric: 'Open Disputes', Value: o.openDisputes, Details: `${o.resolvedDisputes} resolved` },
        { Metric: '', Value: '', Details: '' },
        { Metric: 'RECENT ACTIVITY', Value: '', Details: '' },
        ...(activityData?.activities?.slice(0, 50).map(a => ({
            Metric: a.type.toUpperCase(),
            Value: a.message,
            Details: new Date(a.date).toLocaleString()
        })) || [])
    ] : [];

    return (
        <div className="analyticsOverviewTab" id="analytics-overview-report">
            <div className="analyticsExportBar">
                <CSVLink data={csvData} filename="overview_report.csv" className="exportBtn csvBtn">
                    <FiDownload /> Export CSV
                </CSVLink>
                <button onClick={() => exportToPDF('analytics-overview-report', 'overview_report')} className="exportBtn pdfBtn">
                    <FiDownload /> Export PDF
                </button>
            </div>
            <div className="analyticsStatCards">
                {cards.map((c, i) => (
                    <div key={i} className="analyticsStatCard" style={{ borderTopColor: c.color }}>
                        <div className="statCardIcon" style={{ color: c.color, backgroundColor: `${c.color}10` }}>{c.icon}</div>
                        <div className="statCardBody">
                            <div className="statLabel">{c.label}</div>
                            <div className="statValue">{c.value}</div>
                            {c.sub && <div className="statSub">{c.sub}</div>}
                            {c.growth !== undefined && c.growth !== null && (
                                <div className={`statGrowth ${c.growth >= 0 ? 'positive' : 'negative'}`}>
                                    {c.growth >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
                                    {Math.abs(c.growth)}% vs prev period
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="analyticsOverviewBottom">
                <div className="analyticsChartCard" style={{ flex: 2 }}>
                    <h3 className="chartCardTitle">Activity by Hour of Day</h3>
                    <div className="chartContainer" style={{ height: 250 }}>
                        {hourlyChart && <Line data={hourlyChart} options={miniChartOpts} />}
                    </div>
                </div>

                <div className="analyticsChartCard activityFeedCard" style={{ flex: 1 }}>
                    <h3 className="chartCardTitle">Recent Activity</h3>
                    <div className="analyticsActivityFeed">
                        {activityData?.activities?.slice(0, 15).map((a, i) => (
                            <div key={i} className="activityItem">
                                <span className="activityIcon" style={{ backgroundColor: `${activityColors[a.type]}15`, color: activityColors[a.type] }}>
                                    {activityIcons[a.type]}
                                </span>
                                <div className="activityBody">
                                    <div className="activityMsg">{a.message}</div>
                                    <div className="activityTime">{new Date(a.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                                </div>
                            </div>
                        ))}
                        {(!activityData?.activities || activityData.activities.length === 0) && (
                            <div className="noDataMsg">No recent activity</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AnalyticsOverview;
